/**
 * The public pre-event page (DEC-016): what's on, where, and the tickets on sale.
 *
 * Reached by a shared link (`/tickets/<eventId>`) or an invite email. Anyone may buy without
 * an account — an email address is the whole identity, because it is where the tickets go.
 * A *remembered* donor is met halfway (owner-directed 2026-08-18): their email and name
 * prefill, their saved card pays in one tap, and the profile door rides the header like it
 * does on every other donor page. Every money surface repeats the server's admission notice:
 * a ticket is not a donation.
 *
 * Desktop uses the room it has: the story of the event (overview, lineup, agenda, FAQs) on
 * the left, the ticket box on the right; on a phone the same pieces stack.
 */
import { Speakers } from "./Speakers.js";
import { useEffect, useRef, useState } from "react";
import {
  AGE_POLICY_LABELS,
  PARKING_LABELS,
  TICKET_INVITE_COPY,
  formatMoney,
  ticketPriceCarriesAppInvite,
  type DirectoryEntry,
  type DonorSessionResponse,
  type SavedInstrument,
  type TicketOrderResponse,
  type TicketPageResponse,
  type TicketTierWire,
} from "@givehere/contracts";
import {
  ApiError,
  apiUrl,
  createTicketOrder,
  fetchNearby,
  fetchTicketPage,
  newIdempotencyKey,
} from "./api.js";
import { DonorPanel } from "./DonorPanel.js";
import { Explain } from "./Explain.js";
import {
  fetchDonorSession,
  fetchPaymentMethods,
  forgetToken,
  storedToken,
} from "./donor-session.js";
import { MenuIcon, PersonIcon } from "./icons.js";
import { ONLINE_LINK_REL, drawableOnlineUrl } from "./online-link.js";
import { instrumentLabel } from "./PaymentSheet.js";
import { StripeConfirm } from "./StripeConfirm.js";
import { TicketOrderView } from "./TicketOrderPage.js";

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * GH-884: the guest emails a buyer filled in, for the tickets after their own — blank fields
 * dropped. A half-typed address is `incomplete` (the button waits, nothing is said); one that
 * repeats another or is the buyer's own is `repeated` (said, because the buyer can't guess it).
 */
export function guestEmailsToSend(
  fields: readonly string[],
  quantity: number,
  purchaserEmail: string,
): { readonly emails: string[] } | { readonly problem: "incomplete" | "repeated" } {
  const given = fields
    .slice(0, Math.max(0, quantity - 1))
    .map((email) => (email ?? "").trim())
    .filter(Boolean);
  if (given.some((email) => !EMAIL_SHAPE.test(email))) return { problem: "incomplete" };
  const lowered = given.map((email) => email.toLowerCase());
  const repeated =
    new Set(lowered).size !== lowered.length ||
    lowered.includes(purchaserEmail.trim().toLowerCase());
  return repeated ? { problem: "repeated" } : { emails: given };
}

export function TicketsPage({ eventId }: { eventId: string }) {
  const [page, setPage] = useState<TicketPageResponse | null>(null);
  const [missing, setMissing] = useState(false);
  const [ended, setEnded] = useState(false);

  const [tierId, setTierId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  /** GH-884: one optional email per ticket after the buyer's own (index 0 is ticket 2). */
  const [guestEmails, setGuestEmails] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shared, setShared] = useState(false);

  /** The remembered donor, loaded quietly: absence is a normal state, never an error. */
  const [donorToken, setDonorToken] = useState<string | null>(null);
  const [donorSession, setDonorSession] = useState<DonorSessionResponse | null>(null);
  /** Saved cards for one-tap purchase; tickets ride the card rail only. */
  const [savedCards, setSavedCards] = useState<readonly SavedInstrument[]>([]);
  const [panelView, setPanelView] = useState<"profile" | "menu" | null>(null);
  const [panelEntries, setPanelEntries] = useState<readonly DirectoryEntry[]>([]);

  /** A placed order: pending shows the card form, paid shows the tickets. */
  const [order, setOrder] = useState<TicketOrderResponse | null>(null);
  const idempotencyKey = useRef(newIdempotencyKey());

  useEffect(() => {
    fetchTicketPage(eventId)
      .then(setPage)
      .catch((caught: unknown) =>
        // Archived (GH-863) reads as ended, calmly; anything else as before.
        caught instanceof ApiError && caught.code === "event_unavailable"
          ? setEnded(true)
          : setMissing(true),
      );
  }, [eventId]);

  useEffect(() => {
    const token = storedToken();
    if (!token) return;
    let cancelled = false;
    fetchDonorSession(token)
      .then((session) => {
        if (cancelled) return;
        setDonorToken(token);
        setDonorSession(session);
        // Prefill, never overwrite: a half-typed address the donor chose wins.
        setEmail((current) => current || (session.profile.email ?? ""));
        setName((current) => current || (session.profile.displayName ?? ""));
        return fetchPaymentMethods(token).then((known) => {
          if (!cancelled) {
            setSavedCards(known.instruments.filter((instrument) => instrument.kind === "card"));
          }
        });
      })
      .catch(() => {
        if (!cancelled) forgetToken();
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The panel's event picker needs a directory; fetched when the sheet opens so the event
  // page itself never pays for it.
  useEffect(() => {
    if (panelView === null || panelEntries.length > 0) return;
    let cancelled = false;
    fetchNearby(null, 25)
      .then((response) => {
        if (!cancelled) setPanelEntries(response.results);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [panelView, panelEntries.length]);

  if (ended) {
    return (
      <main className="card card--landing">
        <h1>This event has ended</h1>
        <p className="muted">It is no longer available.</p>
        <a className="link link--center" href="/">
          Find an event
        </a>
      </main>
    );
  }
  if (missing) {
    return (
      <main className="card">
        <p className="notice">Event not found.</p>
        <a className="link link--center" href="/">
          Find an event
        </a>
      </main>
    );
  }
  if (!page) {
    return (
      <main className="card">
        <p className="muted">Loading…</p>
      </main>
    );
  }

  const buyable = page.tiers.filter((tier) => tier.status === "on_sale");
  const selected = buyable.find((tier) => tier.id === tierId) ?? buyable[0] ?? null;
  const isFree = selected ? BigInt(selected.price.amountMinor) === 0n : false;
  const totalLabel = selected
    ? formatMoney({
        amountMinor: (BigInt(selected.price.amountMinor) * BigInt(quantity)).toString(10),
        currency: selected.price.currency,
      })
    : "";
  // GH-888 (owner: "paid only"): an invite comes with a paid ticket only, so a free tier neither
  // promises one nor asks for guests' emails — the server refuses guests on a free order anyway.
  const invitesIncluded =
    page.appInvites.included &&
    selected !== null &&
    ticketPriceCarriesAppInvite(selected.price.amountMinor);
  const offersGuests = invitesIncluded && page.appInvites.guestEmails && quantity > 1;
  const guests = offersGuests
    ? guestEmailsToSend(guestEmails, quantity, email)
    : { emails: [] as string[] };
  const guestsToSend = "emails" in guests ? guests.emails : null;
  const canSubmit =
    selected !== null &&
    EMAIL_SHAPE.test(email.trim()) &&
    guestsToSend !== null &&
    !busy &&
    (isFree || page.paidTicketsAvailable) &&
    (selected.quantityAvailable === null || selected.quantityAvailable >= quantity);

  const when = page.startsAt
    ? new Date(page.startsAt).toLocaleString([], { dateStyle: "full", timeStyle: "short" })
    : null;
  const onlineUrl = drawableOnlineUrl(page.onlineUrl);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: page.eventName, url });
      } else {
        await navigator.clipboard.writeText(url);
        setShared(true);
        setTimeout(() => setShared(false), 2500);
      }
    } catch {
      // Cancelled a share sheet — nothing to report.
    }
  };

  const submit = async (instrument?: SavedInstrument) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const placed = await createTicketOrder(
        eventId,
        {
          tierId: selected.id,
          quantity,
          purchaserEmail: email.trim(),
          ...(name.trim() ? { purchaserName: name.trim() } : {}),
          ...(instrument ? { savedInstrumentId: instrument.id } : {}),
          ...(guestsToSend && guestsToSend.length > 0 ? { guestEmails: guestsToSend } : {}),
        },
        idempotencyKey.current,
        // The bearer rides along whenever the donor is remembered, so even a typed-card
        // order lands on their profile; it is required for the saved-card path.
        donorToken,
      );
      // A saved-card charge that needs no browser step is on its way: the webhook issues
      // the tickets and the order view polls until they exist.
      if (instrument && placed.status === "pending" && !placed.requiresAction) {
        setOrder({ ...placed, status: "paid" });
      } else {
        setOrder(placed);
      }
      // The next order (another tier, a friend's tickets) is a new payment.
      idempotencyKey.current = newIdempotencyKey();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "The order was not placed.");
    } finally {
      setBusy(false);
    }
  };

  // Paid and confirmed (or free): show the tickets right here — same view as the email link.
  if (order && order.status !== "pending") {
    return <TicketOrderView orderId={order.orderId} accessToken={order.accessToken} />;
  }

  const goodToKnow: string[] = [];
  if (page.doorsOpenMinutes !== null) {
    goodToKnow.push(`Doors open ${page.doorsOpenMinutes} minutes before start`);
  }
  if (page.agePolicy) goodToKnow.push(AGE_POLICY_LABELS[page.agePolicy]);
  if (page.parking) goodToKnow.push(PARKING_LABELS[page.parking]);

  const primaryCard = savedCards[0] ?? null;

  return (
    <main className="card card--event">
      {page.heroImageUrl && (
        <img className="tickets__hero" src={apiUrl(page.heroImageUrl)} alt="" />
      )}
      <header className="tickets__head">
        <button
          type="button"
          className="org__corner org__corner--menu"
          aria-label="Menu"
          onClick={() => setPanelView("menu")}
        >
          <MenuIcon />
        </button>
        <button
          type="button"
          className={
            donorSession
              ? "org__corner org__corner--profile org__corner--in"
              : "org__corner org__corner--profile"
          }
          aria-label={donorSession ? "Your profile" : "Check in at an event"}
          onClick={() => setPanelView("profile")}
        >
          <PersonIcon />
          {donorSession && <span className="home__profile-dot" aria-hidden="true" />}
        </button>
        <p className="muted">{page.organizationDisplayName}</p>
        <h1 className="tickets__title" dir="auto">
          {page.eventName}
        </h1>
        {when && <p className="muted">{when}</p>}
        {(page.location ?? page.address) && (
          <p className="muted">{page.location ?? page.address}</p>
        )}
        {/* A hybrid event's online link (GH-866). */}
        {onlineUrl !== null && (
          <p>
            <a className="link" href={onlineUrl} target="_blank" rel={ONLINE_LINK_REL}>
              Join online
            </a>
          </p>
        )}
        <button type="button" className="link link--center" onClick={() => void share()}>
          {shared ? "Link copied!" : "Share this event"}
        </button>
      </header>

      <div className="tickets__layout">
        <div className="tickets__about">
          {page.overview && <p className="tickets__overview">{page.overview}</p>}

          {goodToKnow.length > 0 && (
            <section className="tickets__facts">
              <h2 className="tickets__section">Good to know</h2>
              <ul className="tickets__chips">
                {goodToKnow.map((fact) => (
                  <li key={fact}>{fact}</li>
                ))}
              </ul>
            </section>
          )}

          <Speakers speakers={page.lineup} headingClassName="tickets__section" />

          {page.agenda.length > 0 && (
            <section>
              <h2 className="tickets__section">Agenda</h2>
              <ul className="tickets__agenda">
                {page.agenda.map((item, index) => (
                  <li key={index}>
                    {(item.startTime ?? item.endTime) && (
                      <span className="tickets__agenda-time">
                        {[item.startTime, item.endTime].filter(Boolean).join(" – ")}
                      </span>
                    )}
                    <strong>{item.title}</strong>
                    {item.description && <span className="muted"> — {item.description}</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {page.faqs.length > 0 && (
            <section>
              <h2 className="tickets__section">Frequently asked questions</h2>
              {page.faqs.map((faq, index) => (
                <details key={index} className="tickets__faq">
                  <summary>{faq.question}</summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </section>
          )}
        </div>

        <aside className="tickets__buy">
          {buyable.length === 0 && (
            <p className="notice">
              {page.eventStatus === "ended" ? "This event has ended." : "Not on sale yet."}
            </p>
          )}

          {buyable.length > 0 && (
            <>
              <h2 className="tickets__section">Tickets</h2>
              <div className="tickets__tiers">
                {buyable.map((tier) => (
                  <TierRow
                    key={tier.id}
                    tier={tier}
                    selected={selected?.id === tier.id}
                    onPick={() => {
                      setTierId(tier.id);
                      setQuantity(1);
                    }}
                  />
                ))}
              </div>

              {selected && (
                <>
                  <label className="field">
                    <span>How many</span>
                    <select
                      value={quantity}
                      onChange={(changed) => setQuantity(Number(changed.target.value))}
                    >
                      {Array.from(
                        { length: Math.min(10, selected.quantityAvailable ?? 10) },
                        (_, index) => index + 1,
                      ).map((count) => (
                        <option key={count} value={count}>
                          {count}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span>Email for your tickets</span>
                    <input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={email}
                      onChange={(changed) => setEmail(changed.target.value)}
                    />
                  </label>
                  <label className="field">
                    <span>Name — optional</span>
                    <input
                      autoComplete="name"
                      value={name}
                      onChange={(changed) => setName(changed.target.value)}
                    />
                  </label>

                  {/* GH-884, GH-888: every paid ticket comes with an app invite while invite-only is on. */}
                  {invitesIncluded && (
                    <p className="tickets__invite">{TICKET_INVITE_COPY.included}</p>
                  )}
                  {offersGuests && (
                    <fieldset className="tickets__guests">
                      <legend className="visually-hidden">{TICKET_INVITE_COPY.guestsLabel}</legend>
                      <Explain
                        className="tickets__guests-head"
                        label={TICKET_INVITE_COPY.guestsLabel}
                        title={TICKET_INVITE_COPY.guestsLabel}
                      >
                        <p>{TICKET_INVITE_COPY.guestsExplain}</p>
                      </Explain>
                      {Array.from({ length: quantity - 1 }, (_, index) => (
                        <label className="field" key={index}>
                          <span>{TICKET_INVITE_COPY.guestField(index + 1)}</span>
                          <input
                            type="email"
                            inputMode="email"
                            autoComplete="off"
                            value={guestEmails[index] ?? ""}
                            onChange={(changed) => {
                              const value = changed.target.value;
                              // Dense, never holey: guest 2 may be typed before guest 1.
                              setGuestEmails((current) =>
                                Array.from(
                                  { length: Math.max(current.length, index + 1) },
                                  (_, at) => (at === index ? value : (current[at] ?? "")),
                                ),
                              );
                            }}
                          />
                        </label>
                      ))}
                      {"problem" in guests && guests.problem === "repeated" && (
                        <p className="notice notice--error" role="alert">
                          Each guest needs a different email, not yours.
                        </p>
                      )}
                    </fieldset>
                  )}

                  {!isFree && !page.paidTicketsAvailable && (
                    <p className="notice">Paid tickets are unavailable.</p>
                  )}

                  {!isFree && page.paidTicketsAvailable && primaryCard && (
                    <button
                      type="button"
                      className="pay"
                      disabled={!canSubmit}
                      onClick={() => void submit(primaryCard)}
                    >
                      {busy ? "Working…" : `Pay ${totalLabel} with ${instrumentLabel(primaryCard)}`}
                    </button>
                  )}

                  <button
                    type="button"
                    className={!isFree && primaryCard ? "link link--center" : "pay"}
                    disabled={!canSubmit}
                    onClick={() => void submit()}
                  >
                    {busy
                      ? "Working…"
                      : isFree
                        ? `Get ${quantity} free ${quantity === 1 ? "ticket" : "tickets"}`
                        : primaryCard
                          ? "Use a different card"
                          : `Buy ${quantity} ${quantity === 1 ? "ticket" : "tickets"} · ${totalLabel}`}
                  </button>
                </>
              )}

              {error && (
                <p className="notice notice--error" role="alert">
                  {error}
                </p>
              )}

              {/* DEC-016: the characterization, server-composed, on the money surface itself. */}
              <p className="tickets__notice">{page.admissionNotice}</p>
            </>
          )}
        </aside>
      </div>

      {order && order.status === "pending" && order.clientSecret && (
        <StripeConfirm
          clientSecret={order.clientSecret}
          amountLabel={formatMoney(order.amount)}
          organizationDisplayName={page.organizationDisplayName}
          onAuthorized={() => {
            // The webhook issues the tickets; the order view polls until they exist.
            setOrder({ ...order, status: "paid" });
          }}
          onCancel={() => setOrder(null)}
        />
      )}

      {panelView !== null && (
        <DonorPanel
          token={donorToken}
          session={donorSession}
          view={panelView}
          eventName={page.eventName}
          menuLinks={[
            // Retargeted 2026-09-02: `/e/<id>` is now the donor's event page rather than the
            // hall's projection, so the label follows the destination. A phone-sized menu
            // offering the projector layout was always a compromise; it is now simply wrong.
            { href: `/e/${encodeURIComponent(eventId)}`, label: "Event page", external: true },
            { href: "/", label: "Find another event" },
          ]}
          entries={panelEntries}
          onSession={(token, session) => {
            setDonorToken(token);
            setDonorSession(session);
            setEmail((current) => current || (session.profile.email ?? ""));
            setName((current) => current || (session.profile.displayName ?? ""));
          }}
          onSignedOut={() => {
            setDonorToken(null);
            setDonorSession(null);
            setSavedCards([]);
            setPanelView(null);
          }}
          onDismiss={() => setPanelView(null)}
        />
      )}
    </main>
  );
}

function TierRow({
  tier,
  selected,
  onPick,
}: {
  tier: TicketTierWire;
  selected: boolean;
  onPick: () => void;
}) {
  const soldOut = tier.quantityAvailable !== null && tier.quantityAvailable === 0;
  const free = BigInt(tier.price.amountMinor) === 0n;
  return (
    <button
      type="button"
      className={selected ? "tier tier--selected" : "tier"}
      /*
        Which tier is chosen was expressed only in the stylesheet, so it was not expressed to
        anyone not looking at it: a screen reader read four identical buttons at every setting.
        The amount chips on the checkout have carried `aria-pressed` all along; this screen was
        written later and did not inherit it.
      */
      aria-pressed={selected}
      disabled={soldOut}
      onClick={onPick}
    >
      <span className="tier__name">
        <span dir="auto">{tier.name}</span>
        {tier.description && (
          <span className="tier__description" dir="auto">
            {tier.description}
          </span>
        )}
        {/*
          The gift inside the purchase (DEC-025). Server-composed, and null where there is
          nothing to disclose — a free tier and a tier worth its price both stay silent rather
          than carrying a sentence that says "no". The full §6115 statement follows on the order.
        */}
        {tier.contributionNote && (
          <span className="tier__contribution">{tier.contributionNote}</span>
        )}
      </span>
      <span className="tier__price">
        {soldOut ? "Sold out" : free ? "Free" : formatMoney(tier.price)}
        {tier.seatsPerTicket > 1 && <span className="tier__left">seats {tier.seatsPerTicket}</span>}
        {!soldOut && tier.quantityAvailable !== null && tier.quantityAvailable <= 10 && (
          <span className="tier__left">{tier.quantityAvailable} left</span>
        )}
      </span>
    </button>
  );
}
